import alexanderAvatar from '@/images/stories/alexander.png';
import rosaAvatar from '@/images/stories/rosa.png';
import alenaAvatar from '@/images/stories/alena.png';

/**
 * Success story content model (SPA — not Nuxt Content).
 *
 * Each story:
 *   author  — { name, role, company?, avatar? }  // avatar: optional @/images/... path
 *   lead    — full intro paragraph under the title (plain text or HTML)
 *   leadHtml — set true when lead contains HTML (e.g. links)
 *   excerpt — short summary for cards, meta, and SEO
 *   blocks  — ordered content blocks:
 *     { type: 'heading', id: 'slug-for-anchor', text: '...' }
 *     { type: 'paragraph', text: '...' }
 *     { type: 'paragraph', text: '...<a href="...">...</a>', html: true }
 *     { type: 'quote', text: '...', author?: { ... } }  // author defaults to story.author
 */

export const successStories = [
  {
    slug: 'senior-designer-no-manual-extraction',
    title: 'How a senior designer eliminated manual color extraction with Site Palette',
    author: {
      name: 'Alexander Gladilin',
      role: 'Senior Digital Designer',
      company: 'Sliday',
      avatar: alexanderAvatar,
    },
    footerLabel: 'Senior designer: No manual extraction',
    excerpt:
      'At a fast-paced agency, a senior designer cut client color prep from 20 minutes of DevTools and eyedropper work to seconds with Site Palette — accurate palettes, Figma import, and shareable links for the whole team.',
    leadHtml: true,
    lead:
      'Alexander Gladilin is a Senior Digital Designer at <a href="https://sliday.com/" target="_blank" rel="noopener noreferrer">Sliday</a>, a dynamic digital agency where he brings complex web, product, and branding projects to life. His role requires constantly shifting gears between diverse clients, meaning efficiency isn\'t just a bonus — it\'s an absolute necessity. To keep up with tight deadlines without sacrificing quality, he is always on the lookout for tools that eliminate administrative busywork and protect his creative time.',
    blocks: [
      {
        type: 'heading',
        id: 'the-challenge',
        text: 'The challenge: the tedious color hunt',
      },
      {
        type: 'paragraph',
        text: 'Working at a fast-paced agency, Alexander tackles numerous web design, branding, and product redesign projects. Whenever a new client came onboard, the first step was always the same: understanding and adapting their existing brand colors.',
      },
      {
        type: 'paragraph',
        text: 'In the past, this meant a tedious, manual workflow. Alexander had to dig through CSS files via Developer Tools, take screenshots of the client\'s website, and painstakingly use the eyedropper tool in Figma to capture the hex codes. Not only was this process time-consuming, but it also left room for inconsistencies — especially when trying to extract secondary colors, gradients, or hover states that weren\'t immediately obvious.',
      },
      {
        type: 'quote',
        text: 'Before, I\'d spend up to 20 minutes just trying to build a comprehensive color library from a client\'s live website. When you multiply that across multiple projects and design iterations, you are losing hours of creative time on basic admin work.',
      },
      {
        type: 'heading',
        id: 'the-solution',
        text: 'The solution: seamless extraction with Site Palette',
      },
      {
        type: 'paragraph',
        html: true,
        text: 'Alexander needed a way to automate color extraction without leaving his browser or his design environment. That\'s when he integrated <strong>Site Palette</strong> into his daily workflow.',
      },
      {
        type: 'paragraph',
        text: 'With the Site Palette Chrome extension, Alexander\'s workflow transformed instantly. Instead of hunting for hex codes, he simply navigates to the client\'s website, clicks the extension icon, and Site Palette automatically generates comprehensive color schemes.',
      },
      {
        type: 'quote',
        text: 'The ability to instantly see different palette extractions — like Median Cut, Material Design, and Color Cube — gives me a perfect starting point. It doesn\'t just pull random colors; it ranks them by their on-screen area and extracts the visible CSS colors flawlessly.',
      },
      {
        type: 'paragraph',
        html: true,
        text: 'Site Palette\'s deep integration with design tools took it a step further. Instead of manually copying and pasting hex codes, Alexander utilizes the <strong>Site Palette Figma plugin</strong>. With one click, the generated palettes are imported directly into his Figma workspace, ready to be turned into local color variables.',
      },
      {
        type: 'heading',
        id: 'the-results',
        text: 'The results: faster iterations and perfect consistency',
      },
      {
        type: 'paragraph',
        text: 'By adopting Site Palette, Alexander and the design team at Sliday have significantly streamlined their design operations. What used to take 20 minutes per project now takes just seconds, entirely eliminating the old manual eyedropper workflow.',
      },
      {
        type: 'paragraph',
        text: 'The tool also enables frictionless collaboration. Alexander frequently uses Site Palette\'s shareable links to send palettes to Sliday\'s front-end developers, ensuring the design and engineering teams are always aligned on the exact same hex codes. Furthermore, with built-in support for Figma, Sketch templates, and Adobe Swatches, the transition from browser inspiration to an active design file is completely seamless.',
      },
      {
        type: 'quote',
        text: 'Site Palette does exactly what a great design tool should do: it gets out of your way and lets you focus on creating. It keeps my color workflow close to my design files, moves me from inspiration to execution faster, and guarantees that my client brand colors are 100% accurate every single time.',
      },
      {
        type: 'heading',
        id: 'key-takeaways',
        text: 'Key takeaways from Alexander\'s workflow',
      },
      {
        type: 'paragraph',
        text: 'Alexander\'s experience highlights how effortlessly you can streamline design prep. By using the Chrome extension, you can extract colors from any live URL in seconds. From there, the Figma Community plugin allows you to drop those palettes directly into your canvas so you can work natively. When it is time to hand off, generating unique, shareable links keeps designers, project managers, and developers on the same page.',
      },
      {
        type: 'paragraph',
        html: true,
        text: '<strong>Ready to streamline your design process like Alexander?</strong> <a href="/">Get the Site Palette extension for free.</a>',
      },
    ],
  },
  {
    slug: 'product-designer-instant-moodboards',
    title: 'How a product designer supercharged her moodboarding and competitive analysis with Site Palette',
    author: {
      name: 'Rosa Suleymanova',
      role: 'Product Designer',
      avatar: rosaAvatar,
    },
    footerLabel: 'Product designer: Instant moodboards',
    excerpt:
      'A product designer uses Site Palette to extract competitor color systems in seconds — structured palettes for moodboards, exports to Sketch and Adobe Swatches, and PDF reports for stakeholder reviews.',
    lead:
      'Rosa Suleymanova is a Product Designer specializing in building intuitive SaaS platforms and scaling digital products for early-stage startups. Operating in an environment where speed to market is critical, she is constantly tasked with taking abstract concepts and turning them into polished, user-friendly interfaces. A major part of her role involves establishing the visual direction for entirely new products from scratch. To do this effectively, she needs to stay inspired, analyze industry trends, and present visual concepts to stakeholders quickly, all without getting bogged down in tedious manual workflows.',
    blocks: [
      {
        type: 'heading',
        id: 'the-challenge',
        text: 'The challenge: the fragmented inspiration process',
      },
      {
        type: 'paragraph',
        text: 'Whenever Rosa started a new product design phase, her first step was deep competitive analysis and moodboarding. She needed to understand how industry leaders were utilizing color to establish trust, hierarchy, and accessibility in their dashboards.',
      },
      {
        type: 'paragraph',
        text: 'Historically, this meant a clunky, disjointed research phase. Rosa would take dozens of screenshots of different web applications, paste them into a massive Figma file, and manually use the eyedropper tool to pull out primary, secondary, and accent colors to see how they interacted. Not only was this incredibly time-consuming, but the screenshots would often include shadows, overlays, or varied opacities that made extracting the true base CSS colors nearly impossible. Building a clean, comparable visual landscape of competitor color systems took hours of tedious pixel-hunting.',
      },
      {
        type: 'quote',
        text: 'I was spending entirely too much time trying to reverse-engineer the color systems of other products. I just wanted to pull the raw data so I could see exactly what shades of blue fintech companies were using, or how productivity tools balanced their alert colors, but I was stuck doing manual data entry.',
      },
      {
        type: 'heading',
        id: 'the-solution',
        text: 'The solution: capturing inspiration on the fly',
      },
      {
        type: 'paragraph',
        html: true,
        text: 'Rosa needed a tool that could bridge the gap between her browser-based research and her design canvas. When she discovered <strong>Site Palette</strong>, her entire approach to competitive analysis shifted.',
      },
      {
        type: 'paragraph',
        text: 'Instead of cluttering her hard drive with screenshots, Rosa now uses the Site Palette Chrome extension as her primary research companion. As she navigates through competitor platforms or beautifully designed websites she wants to use for inspiration, she simply clicks the extension. Within seconds, Site Palette extracts the exact CSS colors, organizing them into logical, actionable palettes.',
      },
      {
        type: 'quote',
        text: 'The Color Cube and Median Cut features are absolute game-changers for research. I don\'t just get a massive dump of every color on the page; I get a structured hierarchy of the dominant colors being used. It completely demystifies how complex platforms are structuring their visual hierarchy.',
      },
      {
        type: 'heading',
        id: 'the-results',
        text: 'The results: from research to design system in record time',
      },
      {
        type: 'paragraph',
        text: 'By integrating Site Palette into her discovery phase, Rosa has drastically accelerated her design process. What used to be a multi-day task of building moodboards and competitive color matrices is now completed in a fraction of the time.',
      },
      {
        type: 'paragraph',
        text: 'Because Site Palette allows her to export these palettes directly into Sketch or as Adobe Swatches, she can instantly apply real-world, tested color ratios to her low-fidelity wireframes. When presenting moodboards to founders and stakeholders, she uses Site Palette\'s auto-generated PDF reports, providing a clean, professional breakdown of the visual landscapes they are discussing.',
      },
      {
        type: 'quote',
        text: 'Site Palette has completely transformed how I handle visual research. It allows me to capture inspiration instantly and transition from analyzing a competitor to building out my own design system in Figma seamlessly. It\'s an indispensable part of my product design toolkit.',
      },
      {
        type: 'heading',
        id: 'key-takeaways',
        text: 'Key takeaways from Rosa\'s workflow',
      },
      {
        type: 'paragraph',
        text: 'Rosa\'s experience demonstrates how powerful automated extraction can be for research and moodboarding. By leveraging the browser extension, product designers can effortlessly reverse-engineer complex color systems during competitive analysis. The ability to export palettes directly into native design tools or as professional PDF reports means you can seamlessly move from gathering inspiration to presenting concepts and building out foundational design systems.',
      },
      {
        type: 'paragraph',
        html: true,
        text: '<strong>Ready to streamline your design research like Rosa?</strong> <a href="/">Get the Site Palette extension for free.</a>',
      },
    ],
  },
  {
    slug: 'junior-ux-ui-zero-hex-typos',
    title: 'How a junior UX/UI designer mastered color consistency with the Site Palette Figma plugin',
    author: {
      name: 'Alena Yunyazova',
      role: 'Junior UX/UI Designer',
      avatar: alenaAvatar,
    },
    footerLabel: 'Junior UX/UI: Zero hex typos',
    excerpt:
      'A junior UX/UI designer uses the Site Palette Figma plugin to import extracted palettes straight onto the canvas — no hex copy-paste, organized swatches, and local variables from day one.',
    lead:
      'Alena Yunyazova is a Junior UX/UI Designer who is passionate about creating clean, accessible, and modern digital experiences. As someone early in her design career, she is highly focused on mastering industry-standard tools and building habits that lead to scalable, professional design files. Whether she is working on freelance projects for local businesses or building out case studies for her portfolio, she knows that a well-organized Figma file is just as important as the final visual output. However, navigating the transition from raw creative inspiration to a structured design system can be a daunting process, often requiring tedious manual setup that disrupts her creative flow and slows down her learning process.',
    blocks: [
      {
        type: 'heading',
        id: 'the-challenge',
        text: 'The challenge: the friction of manual file setup',
      },
      {
        type: 'paragraph',
        text: 'When Alena started setting up new Figma files, she frequently found herself bogged down by the manual process of building color libraries. After finding a color scheme she loved on a live website, she would keep a separate browser window open, painstakingly copying and pasting six or seven different hex codes into Figma text layers. From there, she had to manually draw out rectangles, apply the fills, and individually save each one as a local color style or variable. This repetitive data entry wasn\'t just boring; it was highly prone to human error. A single mistyped character in a hex code could throw off the entire visual harmony of an interface, and the constant back-and-forth window switching completely derailed her creative focus.',
      },
      {
        type: 'quote',
        text: 'I was spending the first hour of every new project just trying to get my colors organized on the canvas. As a junior designer, I want to spend my time learning layout, typography, and interaction design, not acting as a data entry clerk for hex codes.',
      },
      {
        type: 'heading',
        id: 'the-solution',
        text: 'The solution: native integration in the design canvas',
      },
      {
        type: 'paragraph',
        html: true,
        text: 'Looking for a way to streamline her file setup, Alena discovered the <strong>Site Palette Figma plugin</strong> and integrated it directly into her design workspace. Instead of treating color extraction and color application as two separate, disconnected tasks, the plugin unified them perfectly.',
      },
      {
        type: 'paragraph',
        text: 'After using the Site Palette Chrome extension to grab the colors from a reference site, she simply opens the plugin right inside her Figma file. With a single click, all of her saved palettes sync directly to her current project.',
      },
      {
        type: 'quote',
        text: 'The magic happens the moment I click import. The plugin automatically generates a beautifully organized frame on my canvas containing all the extracted color swatches, complete with their exact hex values visually labeled. It takes all the guesswork out of the equation.',
      },
      {
        type: 'heading',
        id: 'the-results',
        text: 'The results: instant organization and total accuracy',
      },
      {
        type: 'paragraph',
        text: 'By making the Site Palette Figma plugin a core part of her workflow, Alena has completely eliminated the manual labor of setting up color styles. With her swatches instantly generated on the canvas, she can immediately turn them into Figma local variables without typing a single number.',
      },
      {
        type: 'paragraph',
        text: 'As a junior designer, this automation has given her a massive confidence boost, ensuring her foundational design files are structured just as cleanly as those of senior designers. Furthermore, she can iterate on color themes rapidly. If a client wants to pivot to a different visual direction based on a competitor\'s site, Alena can import the new palette via the plugin in seconds, keeping her projects moving forward without the friction of manual updates.',
      },
      {
        type: 'quote',
        text: 'Site Palette has made me so much faster and more confident in Figma. I never worry about making a typo in a hex code anymore, and my files are incredibly organized from the very first click. It\'s the ultimate shortcut for setting up a design system.',
      },
      {
        type: 'heading',
        id: 'key-takeaways',
        text: 'Key takeaways from Alena\'s workflow',
      },
      {
        type: 'paragraph',
        text: 'Alena\'s experience illustrates how crucial the right integrations are for setting up professional design files quickly and accurately. By utilizing the Site Palette Figma plugin, designers can bypass the tedious process of copying and pasting hex codes across different windows entirely. Bringing extracted palettes directly onto the canvas allows for the immediate creation of local variables and styles, ensuring perfect accuracy and a highly organized workspace from day one.',
      },
      {
        type: 'paragraph',
        html: true,
        text: '<strong>Ready to streamline your design process like Alena?</strong> <a href="/">Get the Site Palette extension for free.</a>',
      },
    ],
  },
];

export function getSuccessStoryBySlug(slug) {
  return successStories.find((story) => story.slug === slug);
}
